import React, { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { ScanLine, Package, Plus, CheckCircle, AlertTriangle, Loader2, PlusCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import BarcodeScanner from './BarcodeScanner';

export default function ScanAssetLookup({ onAssetFound, customerId, customers = [], alreadyLinkedIds = [], buttonLabel, buttonVariant = 'outline', buttonSize = 'sm', className = '' }) {
  const [resultsOpen, setResultsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [scannedValue, setScannedValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAsset, setNewAsset] = useState({ name: '', asset_type: 'device', serial_number: '', manufacturer: '', model: '', status: 'active', customer_id: '' });

  const handleScan = async (value) => {
    if (!value) return;
    setScannedValue(value);
    setShowCreate(false);
    setLoading(true);
    setResultsOpen(true);
    try {
      const params = { q: value };
      if (customerId) params.customer_id = customerId;
      const res = await api.get('/assets/lookup-by-scan', { params });
      setResults(res.data.results || []);
    } catch {
      toast.error('Failed to look up scanned value');
      setResults([]);
    }
    setLoading(false);
  };

  const handleSelect = (asset) => {
    onAssetFound(asset);
    setResultsOpen(false);
    setResults([]);
    toast.success(`Linked: ${asset.name}`);
  };

  const handleShowCreate = () => {
    setNewAsset({
      name: '',
      asset_type: 'device',
      serial_number: scannedValue,
      manufacturer: '',
      model: '',
      status: 'active',
      customer_id: customerId || ''
    });
    setShowCreate(true);
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    if (!newAsset.name.trim()) { toast.error('Asset name is required'); return; }
    setCreating(true);
    try {
      const payload = { ...newAsset };
      if (payload.customer_id) {
        const cust = customers.find(c => c.id === payload.customer_id);
        if (cust) payload.customer_name = cust.company_name;
      }
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const res = await api.post('/assets', payload);
      toast.success(`Asset "${res.data.name}" created`);
      onAssetFound(res.data);
      setResultsOpen(false);
      setShowCreate(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : detail || 'Failed to create asset');
    }
    setCreating(false);
  };

  const statusColor = (s) => ({
    active: 'bg-emerald-100 text-emerald-700',
    maintenance: 'bg-amber-100 text-amber-700',
    inactive: 'bg-gray-100 text-gray-600',
    retired: 'bg-red-100 text-red-700'
  })[s] || 'bg-gray-100 text-gray-600';

  return (
    <>
      <BarcodeScanner
        onScan={handleScan}
        buttonLabel={buttonLabel || 'Scan'}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        className={className}
      />

      <Dialog open={resultsOpen} onOpenChange={(v) => { setResultsOpen(v); if (!v) setShowCreate(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5" /> {showCreate ? 'Quick-Create Asset' : 'Scan Results'}
            </DialogTitle>
          </DialogHeader>

          {!showCreate ? (
            <div className="space-y-3">
              <div className="text-sm bg-gray-50 p-2.5 rounded-lg font-mono break-all" data-testid="scan-result-value">
                Scanned: <strong>{scannedValue}</strong>
              </div>

              {loading && (
                <div className="flex items-center justify-center py-6 text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Looking up asset...
                </div>
              )}

              {!loading && results.length === 0 && (
                <div className="flex flex-col items-center py-4 text-gray-500" data-testid="scan-no-results">
                  <AlertTriangle className="h-8 w-8 text-amber-400 mb-2" />
                  <p className="font-medium">No matching assets found</p>
                  <p className="text-xs mt-1 mb-3">This serial number isn't in the system yet</p>
                  <Button onClick={handleShowCreate} className="gap-1.5" data-testid="scan-quick-create-btn">
                    <PlusCircle className="h-4 w-4" /> Create New Asset
                  </Button>
                </div>
              )}

              {!loading && results.length > 0 && (
                <>
                  <div className="space-y-2 max-h-[260px] overflow-y-auto" data-testid="scan-results-list">
                    {results.map((asset) => {
                      const isLinked = alreadyLinkedIds.includes(asset.id);
                      return (
                        <div
                          key={asset.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${isLinked ? 'bg-emerald-50 border-emerald-200' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
                          data-testid={`scan-result-${asset.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="font-medium text-sm truncate">{asset.name}</span>
                              <Badge className={`text-[10px] ${statusColor(asset.status)}`}>{asset.status}</Badge>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5 ml-6">
                              {asset.serial_number && <span>SN: {asset.serial_number}</span>}
                              {asset.asset_type && <span className="ml-2">{asset.asset_type}</span>}
                              {asset.customer_name && <span className="ml-2">| {asset.customer_name}</span>}
                            </div>
                          </div>
                          {isLinked ? (
                            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium flex-shrink-0">
                              <CheckCircle className="h-3.5 w-3.5" /> Linked
                            </span>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => handleSelect(asset)} className="flex-shrink-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-testid={`scan-link-${asset.id}`}>
                              <Plus className="h-4 w-4 mr-1" /> Link
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="pt-2 border-t">
                    <Button variant="outline" size="sm" onClick={handleShowCreate} className="w-full gap-1.5" data-testid="scan-quick-create-btn">
                      <PlusCircle className="h-4 w-4" /> Not here? Create New Asset
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={handleCreateAsset} className="space-y-3" data-testid="scan-create-form">
              <div className="text-xs bg-blue-50 text-blue-700 p-2.5 rounded-lg flex items-center gap-2">
                <ScanLine className="h-4 w-4 flex-shrink-0" />
                Serial number pre-filled from scan: <strong className="font-mono">{scannedValue}</strong>
              </div>

              <div>
                <Label>Asset Name *</Label>
                <Input value={newAsset.name} onChange={(e) => setNewAsset(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Dell Latitude 5540" required data-testid="scan-create-name" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Serial Number</Label>
                  <Input value={newAsset.serial_number} onChange={(e) => setNewAsset(p => ({ ...p, serial_number: e.target.value }))} className="font-mono" data-testid="scan-create-serial" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={newAsset.asset_type} onValueChange={(v) => setNewAsset(p => ({ ...p, asset_type: v }))}>
                    <SelectTrigger data-testid="scan-create-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="device">Device</SelectItem>
                      <SelectItem value="workstation">Workstation</SelectItem>
                      <SelectItem value="server">Server</SelectItem>
                      <SelectItem value="network">Network</SelectItem>
                      <SelectItem value="software">Software</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Manufacturer</Label>
                  <Input value={newAsset.manufacturer} onChange={(e) => setNewAsset(p => ({ ...p, manufacturer: e.target.value }))} placeholder="e.g. Dell, HP, Lenovo" data-testid="scan-create-manufacturer" />
                </div>
                <div>
                  <Label>Model</Label>
                  <Input value={newAsset.model} onChange={(e) => setNewAsset(p => ({ ...p, model: e.target.value }))} placeholder="e.g. Latitude 5540" data-testid="scan-create-model" />
                </div>
              </div>

              {customers.length > 0 && (
                <div>
                  <Label>Customer</Label>
                  <Select value={newAsset.customer_id || '__none'} onValueChange={(v) => setNewAsset(p => ({ ...p, customer_id: v === '__none' ? '' : v }))}>
                    <SelectTrigger data-testid="scan-create-customer"><SelectValue placeholder="Select customer (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">No customer</SelectItem>
                      {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="gap-1.5" data-testid="scan-create-back-btn">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <Button type="submit" disabled={creating} className="flex-1 gap-1.5" data-testid="scan-create-submit-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
                  {creating ? 'Creating...' : 'Create & Link Asset'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
